import { AuthService } from 'phoenix/services';
import { CommonUtilsService } from 'util/services';
import SettingsService from 'phoenix/services/settingsService';

/**
* @ngdoc service
* @name  phoenix.pxAuthInterceptor
* 
* @description 
* An http interceptor that is responsible for adding the 
* PHOENIX token to each API request. If the user is not
* authenticated, the interceptor will redirect them to login
* 
*
* If the request should not include a PHOENIX-TOKEN,
* you may include { skipAuth: true } in the http config used
* for the request.
*

	$http.get('http://localhost/home', {skipAuth: true});

*
**/
export function AuthInterceptor(
	$q:ng.IQService, 
	$location:ng.ILocationService,
	$injector,
	utils: CommonUtilsService
) {
	// Checks if the current request is targeting a whitelisted auth url.
	// This check is used to ensure auth tokens are only sent to specific urls that
	// are configured ahead of time.
	const isWhitelistedRequest = (config) => {
		let urlToCheck = removeFragmentsAndQueryString(config.url);
		
		const whiteListedUrls = [
			settingsService.serverAPIPath,
			settingsService.idp.web.path,
			settingsService.idp.hybrid.path
		];

		return whiteListedUrls.some(whiteListedUrl => {
			return urlToCheck.indexOf(whiteListedUrl) === 0;
		});

		function removeFragmentsAndQueryString(url:string) {
			return url.split('?')[0].split('#')[0];
		}
	};

	/*
	* pxAuthService is lazy loaded via the setAuthService function to eliminate any http 
	* injector circular dependancy when adding this interceptor via a module config block.
	* if a better solution arises it should replace this and the pxAuthService should be 
	* injected into the service
	*/
	let pxAuthService:AuthService;
	let settingsService:SettingsService;

	function requestFn(config) {
		setAuthService();

		// Only send auth token to requests that target phoenix API
		if (!skipAuth(config) && isWhitelistedRequest(config)) {
			if (pxAuthService.token) {
				config.headers.Authorization = pxAuthService.token.type + ' ' + pxAuthService.token.value;
			}
		}

		return config;
	}

	function skipAuth(config) {
		return angular.isDefined(config.skipAuth) && config.skipAuth;
	}

	function responseErrorFn(rejection) {
		setAuthService();

		if (rejection.status === 401 && isWhitelistedRequest(rejection.config)) {
			return pxAuthService.handle401();
		}

		return $q.reject(rejection);
	}

	function redirectPostLogin(redirectUri?: string) {
		setAuthService();
		let redirect = redirectUri || pxAuthService.redirectPath;

		pxAuthService.redirectPath = '/';

		$location.url(redirect);
	}

	function setAuthService() {
		if (!pxAuthService) {
			pxAuthService = $injector.get('pxAuthService');
		}

		if (!settingsService) {
			settingsService = $injector.get('settingsService');
		}
	}

	return {
		/**
		 * @ngdoc method
		 * @name pxAuthInterceptor#request
		 * @methodOf phoenix.pxAuthInterceptor
		 * @kind function
		 *
		 * @description
		 * Adds the PHOENIX token to every request
		 * 
		 */
		request: requestFn,
		/**
		 * @ngdoc method
		 * @name pxAuthInterceptor#responseError
		 * @methodOf phoenix.pxAuthInterceptor
		 * @kind function
		 *
		 * @description
		 * Redirects to /login on 401
		 * 
		 */
		responseError: utils.debounce(responseErrorFn, 300),
		/**
		 * @ngdoc method
		 * @name pxAuthInterceptor#redirectPostLogin
		 * @methodOf phoenix.pxAuthInterceptor
		 * @kind function
		 *
		 * @description
		 * Call this to redirect back to the previous route
		 * after authenticating (deep linking)
		 * 
		 */
		redirectPostLogin
	};
}

AuthInterceptor.$inject = [
	'$q',
	'$location',
	'$injector',
	'utils'
];



// WEBPACK FOOTER //
// ./src/phoenix/Interceptors/authInterceptor.ts